import { GQL_ID, Order, PageInfo, QueryVariablesTypes } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

// Определяем фрагменты напрямую здесь для избежания проблем с импортом
const PRODUCT_BASE_FIELDS = gql`
  fragment ProductBaseFields on Product {
    id
    name
    price
    oldPrice
    price_type
    isWeightProduct
    count_multiplier
    isFavorite
    stock {
      amount
    }
    preview {
      url
    }
    slug
    review_count
    marketingLabels {
      id
      title
      description
      image(style: $style) {
        url
      }
      activeFrom
      activeUntil
      optionColor
      optionBackgroundColor
      optionLink
      optionLinkLabel
    }
    is_alcohol
  }
`


export interface AllMyOrdersQuery {
  allMyOrders: {
    edges: Order[]
    pageInfo: PageInfo
  }
}

export interface AllMyOrdersVariables extends QueryVariablesTypes {
  business_profile_id?: GQL_ID
  onlyActive: boolean
  style?: string
}

const GQL_QUERY_ALL_MY_ORDERS = gql`
  ${PRODUCT_BASE_FIELDS}
  query allMyOrders (
    $filter: NestedFilterInput,
    $pager: CommonPagerInput,
    $sort: CommonSortInput
    $business_profile_id: ID
    $onlyActive: Boolean
    $style: String
  ) {
    allMyOrders (
      filter: $filter,
      pager: $pager,
      sort: $sort,
      onlyActive: $onlyActive
      business_profile_id: $business_profile_id
    ) {
      edges {
        id
        shop_id
        shop {
          id
          name
          address
          status
        }
        status
        total
        assembled_total
        customerFinalCost
        payment_type
        weight
        created_at
        placed_at
        address
        deliveryType
        deliveryCost
        totalCashback

        totalDiscount
        deliverySlot {
          id
          period {
            from
            to
          }
          slotDate
          slotTime
        }

        paymentMethod {
          id  
          type 
          card
          last
          isDefault 
        }

        deliveryZone {
          id
          name
          status
          tzOffset
        }
        meta {
          id
          product_id 
          shop_id
          name
          price
          count
          weight  
          total_weight 
          total
          status
          assembly_method
          assembled_count
          assembled_total
          cashback
          unit_multiplier

          changedBy {  
            id
            product_id
            shop_id
            name
            price
            unit_multiplier
            count
            weight
            total_weight
            total
            status
            assembly_method
            assembled_count
            assembled_weight
            assembled_total
            cashback
            product {
              ...ProductBaseFields
            }
          }

          isChangeTo {  
            id
            product_id
            shop_id
            name
            price
            unit_multiplier
            count
            weight
            total_weight
            total
            status
            assembly_method
            assembled_count
            assembled_weight
            assembled_total
            cashback

            product {
              ...ProductBaseFields
            }
          }

          product {
            ...ProductBaseFields
          }
        }

        recipientInfo {
          firstName
          lastName
          phone
        }

        receipts {
          id
          type
          status
          receiptQueryString
          receiptPreviewUrl
        }
      }
      pageInfo {
        page
        perPage
        lastPage
        next
        total
      }
    }
  }
`

type useFetchAllMyOrdersProps = {
  variables?: AllMyOrdersVariables
  skip?: boolean
}

export const useFetchAllMyOrders = ({
  variables,
  skip
}: useFetchAllMyOrdersProps | undefined = {}) => {
  const { data, loading, error, refetch, fetchMore, ...rest } = useQuery<
    AllMyOrdersQuery,
    AllMyOrdersVariables
  >(GQL_QUERY_ALL_MY_ORDERS, {
    variables,
    skip
  })

  const orders = useMemo(() => data?.allMyOrders?.edges ?? [], [ data?.allMyOrders?.edges ])

  const pageInfo = useMemo(() => data?.allMyOrders?.pageInfo, [ data?.allMyOrders?.pageInfo ])

  const { hasMore, fetchNextPage, clearPagination } = useFetchMorePagination<AllMyOrdersQuery, AllMyOrdersVariables>({
    loading,
    page: pageInfo?.page ?? 1,
    usePager: true,
    pageVariable: 'pager',
    fetchMore,
    updaterFunction (cached: AllMyOrdersQuery, fetched: AllMyOrdersQuery) {
      const prev = cached?.allMyOrders?.edges ?? []
      const curr = fetched?.allMyOrders?.edges ?? []

      return {
        hasMore: curr.length > 0,
        result: {
          ...cached,
          allMyOrders: {
            ...cached.allMyOrders,
            edges: [ ...prev, ...curr ]
          }
        }
      }
    }
  })

  return {
    data,
    orders,
    loading,
    error,
    refetch,
    fetchMore,
    hasMore,
    fetchNextPage,
    clearPagination,
    ...rest
  }
}
